"use client";

/**
 * Small presentational primitives for the mega-menu panels.
 *
 * Every item teaches the platform: monochrome icon, short title, one-sentence
 * value line (≤80 chars, enforced editorially in the i18n files). Hover is the
 * only place color appears — icon flips to the pink accent, title to bright —
 * per the calm enterprise style of the panels. Tokens only; no raw colors.
 */

import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";
import { useLocale, useT } from "@/i18n";
import { resolveHref, type MenuCardItem } from "./menu-data";

const MM = "landingNav.mm.";

/** Rich teaching card: icon box, title, one-liner. ~64px tall. */
export function MenuCard({
  item,
  onNavigate,
}: {
  item: MenuCardItem;
  onNavigate: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const Icon = item.icon;
  return (
    <Link
      href={resolveHref(item.href, locale)}
      onClick={onNavigate}
      className="group flex items-start gap-3 rounded-xl border border-transparent px-3 py-2.5 transition-colors duration-150 hover:border-line-subtle hover:bg-bg-elev/60 focus-visible:border-line-subtle focus-visible:bg-bg-elev/60 focus-visible:outline-none"
    >
      <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-bg-base/60 text-fg-muted transition-colors duration-150 group-hover:text-brand-300 group-focus-visible:text-brand-300">
        <Icon size={15} strokeWidth={1.75} aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-[13.5px] font-medium leading-snug text-fg-dim transition-colors duration-150 group-hover:text-fg-bright group-focus-visible:text-fg-bright">
          {t(`${MM}${item.key}.title`)}
        </span>
        <span className="mt-0.5 block text-[12.5px] leading-snug text-fg-muted">
          {t(`${MM}${item.key}.desc`)}
        </span>
      </span>
    </Link>
  );
}

/** Compact integration row: logo mark, name, one-liner. ~56px tall. */
export function IntegrationItem({
  item,
  onNavigate,
}: {
  item: MenuCardItem;
  onNavigate: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const Icon = item.icon;
  return (
    <Link
      href={resolveHref(item.href, locale)}
      onClick={onNavigate}
      className="group flex items-start gap-2.5 rounded-lg border border-transparent px-2.5 py-2 transition-colors duration-150 hover:border-line-subtle hover:bg-bg-elev/60 focus-visible:border-line-subtle focus-visible:bg-bg-elev/60 focus-visible:outline-none"
    >
      <Icon
        size={15}
        strokeWidth={1.75}
        aria-hidden
        className="mt-[3px] flex-shrink-0 text-fg-muted transition-colors duration-150 group-hover:text-brand-300 group-focus-visible:text-brand-300"
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-medium leading-snug text-fg-dim transition-colors duration-150 group-hover:text-fg-bright group-focus-visible:text-fg-bright">
          {t(`${MM}${item.key}.title`)}
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-fg-muted">
          {t(`${MM}${item.key}.desc`)}
        </span>
      </span>
    </Link>
  );
}

/** Quiet check-list ("Why teams choose FixControl"). */
export function MenuChecklist({ keys, prefix }: { keys: readonly string[]; prefix: string }) {
  const t = useT();
  return (
    <ul className="space-y-2.5">
      {keys.map((k) => (
        <li key={k} className="flex items-center gap-2.5 text-[13px] text-fg-dim">
          <Check size={13} strokeWidth={2} aria-hidden className="flex-shrink-0 text-brand-300" />
          {t(`${MM}${prefix}.${k}`)}
        </li>
      ))}
    </ul>
  );
}

/** Panel-footer call-to-action link: "Explore the platform →". */
export function MenuCta({
  labelKey,
  href,
  onNavigate,
}: {
  labelKey: string;
  href: string;
  onNavigate: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  return (
    <Link
      href={resolveHref(href, locale)}
      onClick={onNavigate}
      className="group inline-flex items-center gap-1.5 rounded-lg text-[13px] font-medium text-fg-dim transition-colors duration-150 hover:text-brand-300 focus-visible:text-brand-300 focus-visible:outline-none"
    >
      {t(`${MM}${labelKey}`)}
      <ArrowRight
        size={13}
        aria-hidden
        className="transition-transform duration-150 group-hover:translate-x-0.5"
      />
    </Link>
  );
}

/** Small mono uppercase column/section heading inside a panel. */
export function MenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-fg-muted">
      {children}
    </div>
  );
}
